import moment, { Moment } from "moment";
import React, { useEffect, useRef, useState } from "react";
import { getUserDateFormat } from "../utils/getUserDateFormat";

type Props = {
    value: string;
    onChange: (value: string) => void;
    minDate?: string;
    maxDate?: string;
    required?: boolean;
};

const CustomDateTimePicker = ({
    value,
    onChange,
    minDate,
    maxDate,
    required = false,
}: Props) => {
    const pickerRef = useRef<HTMLDivElement | null>(null);

    const [formattedValue, setFormattedValue] = useState("");
    const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
    const [currentMonth, setCurrentMonth] = useState<Moment>(moment());
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedHour, setSelectedHour] = useState(12);
    const [selectedMinute, setSelectedMinute] = useState(0);
    const [selectedPeriod, setSelectedPeriod] =
        useState<"AM" | "PM">("AM");

    const normalizeMomentFormat = (fmt: string) =>
        fmt.replace(/yyyy/gi, "YYYY").replace(/dd/gi, "DD");

    const displayFormat = normalizeMomentFormat(getUserDateFormat());

    const displayDateTimeFormat = `${displayFormat} hh:mm A`;

    const isValid = (val: string) =>
        moment(
            val,
            displayDateTimeFormat,
            true
        ).isValid();

    const toISO = (
        date: Moment,
        hour: number,
        minute: number,
        period: "AM" | "PM"
    ) => {
        let h = hour;

        if (period === "PM" && h !== 12) {
            h += 12;
        }

        if (period === "AM" && h === 12) {
            h = 0;
        }

        return date
            .clone()
            .hour(h)
            .minute(minute)
            .second(0)
            .format("YYYY-MM-DDTHH:mm");
    };

    const parse = (val?: string) =>
        val ? moment(val, "YYYY-MM-DD") : null;

    const isInRange = (day: Moment) => {
        const min = parse(minDate);
        const max = parse(maxDate);
        if (min && day.isBefore(min, "day")) return false;
        if (max && day.isAfter(max, "day")) return false;
        return true;
    };

    useEffect(() => {
        if (!value) return;

        const m = moment(value);

        if (!m.isValid()) return;

        setSelectedDate(m.clone());

        setCurrentMonth(m.clone());

        setFormattedValue(
            m.format(displayDateTimeFormat)
        );

        let hour = m.hour();

        const minute = m.minute();

        const period =
            hour >= 12 ? "PM" : "AM";

        hour = hour % 12;

        if (hour === 0) hour = 12;

        setSelectedHour(hour);
        setSelectedMinute(minute);
        setSelectedPeriod(period);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (e: any) => {
            if (!pickerRef.current?.contains(e.target)) {
                setShowCalendar(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const input = e.target;
        const rawValue = input.value;
        let cursor = input.selectionStart || 0;

        const digitsBeforeCursor = rawValue
            .slice(0, cursor)
            .replace(/\D/g, "").length;

        let digits = rawValue.replace(/\D/g, "");

        if (digits.length > 8) {
            digits = digits.slice(0, 8);
        }

        let formatted = "";

        if (digits.length <= 2) {
            formatted = digits;
        } else if (digits.length <= 4) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        } else {
            formatted = `${digits.slice(0, 2)}/${digits.slice(
                2,
                4
            )}/${digits.slice(4, 8)}`;
        }

        if (
            formatted.length === 10 &&
            selectedDate
        ) {
            formatted +=
                " " +
                String(selectedHour).padStart(2, "0") +
                ":" +
                String(selectedMinute).padStart(2, "0") +
                " " +
                selectedPeriod;
        }

        setFormattedValue(formatted);

        let newCursor = digitsBeforeCursor;

        if (newCursor > 2) newCursor++;
        if (newCursor > 4) newCursor++;

        requestAnimationFrame(() => {
            inputRef.current?.setSelectionRange(
                Math.min(newCursor, 10),
                Math.min(newCursor, 10)
            );
        });

        if (digits.length >= 4) {
            const temp = moment(
                formatted.substring(0, displayFormat.length),
                displayFormat,
                false
            );

            if (temp.isValid()) {
                setCurrentMonth(temp);
            }
        }

        if (digits.length === 8) {
            const parsed = moment(
                formatted.substring(0, displayFormat.length),
                displayFormat,
                true
            );

            if (parsed.isValid() && isInRange(parsed)) {
                setSelectedDate(parsed);
                setCurrentMonth(parsed.clone());

                onChange(
                    toISO(
                        parsed,
                        selectedHour,
                        selectedMinute,
                        selectedPeriod
                    )
                );

                setFormattedValue(
                    parsed.format(displayFormat) +
                    " " +
                    String(selectedHour).padStart(2, "0") +
                    ":" +
                    String(selectedMinute).padStart(2, "0") +
                    " " +
                    selectedPeriod
                );
            } else {
                setSelectedDate(null);
            }
        }

        if (formatted === "") {
            setSelectedDate(null);
            setCurrentMonth(moment());
            onChange("");
        }
    };

    const handleDayClick = (day: Moment) => {
        if (!isInRange(day)) return;

        setSelectedDate(day);
        setCurrentMonth(day);

        const formatted =
            day.format(displayFormat) +
            " " +
            String(selectedHour).padStart(2, "0") +
            ":" +
            String(selectedMinute).padStart(2, "0") +
            " " +
            selectedPeriod;

        setFormattedValue(formatted);

        onChange(
            toISO(
                day,
                selectedHour,
                selectedMinute,
                selectedPeriod
            )
        );
    };

    const changeMonth = (offset: number) => {
        setCurrentMonth((prev) => prev.clone().add(offset, "month"));
    };

    const today = moment();

    const renderCells = () => {
        const start = currentMonth.clone().startOf("month").startOf("week");
        const day = start.clone();
        const rows = [];

        for (let i = 0; i < 6; i++) {
            const cols = [];

            for (let j = 0; j < 7; j++) {
                const clone = day.clone();

                const isSelected =
                    selectedDate && clone.isSame(selectedDate, "day");

                const isToday = clone.isSame(today, "day");

                const disabled = !isInRange(clone);

                cols.push(
                    <div
                        key={clone.toString()}
                        onClick={() => !disabled && handleDayClick(clone)}
                        className={`
              p-1 text-center rounded cursor-pointer text-sm
              ${disabled ? "opacity-30 pointer-events-none" : ""}
              ${isSelected ? "bg-primary text-white" : ""}
              ${isToday ? "border border-primary font-semibold" : ""}
              ${!isSelected && !disabled
                                ? "hover:bg-blue-100"
                                : ""
                            }
            `}
                    >
                        {clone.date()}
                    </div>
                );

                day.add(1, "day");
            }

            rows.push(
                <div key={i} className="grid grid-cols-7">
                    {cols}
                </div>
            );
        }

        return rows;
    };

    const years = Array.from({ length: 101 }, (_, i) => {
        return moment().year() - 50 + i;
    });

    const hours = Array.from(
        { length: 12 },
        (_, i) => i + 1
    );

    const minutes = Array.from(
        { length: 60 },
        (_, i) => i
    );

    return (
        <div ref={pickerRef} className="relative w-full">
            <input
                ref={inputRef}
                type="text"
                placeholder={`${displayFormat} hh:mm AM`}
                value={formattedValue}
                onChange={handleInputChange}
                onFocus={() => setShowCalendar(true)}
                maxLength={22}
                required={required}
                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
            />

            {formattedValue.length === 10 && !isValid(formattedValue) && (
                <p className="text-xs text-red-500 mt-1">
                    Invalid date/time
                </p>
            )}

            {showCalendar && (
                <div className="absolute mt-2 bg-white text-black border rounded shadow-lg p-4 z-50 w-[600px]">

                    <div className="flex items-start gap-6">

                        <div className="w-[290px]">

                            <div className="flex justify-between mb-2">

                                <button
                                    type="button"
                                    onClick={() => changeMonth(-1)}
                                    className="px-2 hover:bg-gray-100 rounded"
                                >
                                    ◀
                                </button>

                                <div className="flex gap-2">

                                    <select
                                        value={currentMonth.month()}
                                        onChange={(e) => {

                                            const updated = currentMonth
                                                .clone()
                                                .month(Number(e.target.value));

                                            setCurrentMonth(updated);

                                        }}
                                        className="border rounded px-2 py-1 text-sm"
                                    >
                                        {moment.months().map((month, index) => (
                                            <option
                                                key={month}
                                                value={index}
                                            >
                                                {month}
                                            </option>
                                        ))}
                                    </select>

                                    <select
                                        value={currentMonth.year()}
                                        onChange={(e) => {

                                            const updated = currentMonth
                                                .clone()
                                                .year(Number(e.target.value));

                                            setCurrentMonth(updated);

                                        }}
                                        className="border rounded px-2 py-1 text-sm"
                                    >
                                        {years.map((year) => (
                                            <option
                                                key={year}
                                                value={year}
                                            >
                                                {year}
                                            </option>
                                        ))}
                                    </select>

                                </div>

                                <button
                                    type="button"
                                    onClick={() => changeMonth(1)}
                                    className="px-2 hover:bg-gray-100 rounded"
                                >
                                    ▶
                                </button>

                            </div>

                            <div className="grid grid-cols-7 text-xs text-center mb-1">

                                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                                    <div key={d}>{d}</div>
                                ))}

                            </div>

                            {renderCells()}

                        </div>

                        <div className="border-l pl-4 w-[220px]">

                            <h3 className="font-semibold text-lg mb-6">
                                Time
                            </h3>

                            <div className="flex gap-3 mb-6">

                                <select
                                    value={selectedHour}
                                    onChange={(e) =>
                                        setSelectedHour(Number(e.target.value))
                                    }
                                    className="border rounded-md h-11 px-3"
                                >
                                    {hours.map(hour => (
                                        <option
                                            key={hour}
                                            value={hour}
                                        >
                                            {String(hour).padStart(2, "0")}
                                        </option>
                                    ))}
                                </select>

                                <select
                                    value={selectedMinute}
                                    onChange={(e) =>
                                        setSelectedMinute(Number(e.target.value))
                                    }
                                    className="border rounded-md h-11 px-3"
                                >
                                    {minutes.map(minute => (
                                        <option
                                            key={minute}
                                            value={minute}
                                        >
                                            {String(minute).padStart(2, "0")}
                                        </option>
                                    ))}
                                </select>

                                <select
                                    value={selectedPeriod}
                                    onChange={(e) =>
                                        setSelectedPeriod(
                                            e.target.value as "AM" | "PM"
                                        )
                                    }
                                    className="border rounded-md h-11 px-3"
                                >
                                    <option>AM</option>
                                    <option>PM</option>
                                </select>

                            </div>

                            <div className="flex justify-end gap-3">

                                <button
                                    type="button"
                                    onClick={() => {

                                        setSelectedHour(12);
                                        setSelectedMinute(0);
                                        setSelectedPeriod("AM");

                                    }}
                                    className="px-4 py-2 rounded bg-gray-200"
                                >
                                    Clear
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {

                                        if (!selectedDate) return;

                                        const formatted =
                                            selectedDate.format(displayFormat) +
                                            " " +
                                            String(selectedHour).padStart(2, "0") +
                                            ":" +
                                            String(selectedMinute).padStart(2, "0") +
                                            " " +
                                            selectedPeriod;

                                        setFormattedValue(formatted);

                                        onChange(
                                            toISO(
                                                selectedDate,
                                                selectedHour,
                                                selectedMinute,
                                                selectedPeriod
                                            )
                                        );

                                        setShowCalendar(false);

                                    }}
                                    className="px-4 py-2 rounded bg-primary text-white"
                                >
                                    Done
                                </button>

                            </div>

                        </div>

                    </div>

                </div>
            )}
        </div>
    );
};

export default CustomDateTimePicker;